/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
/* eslint-disable no-use-before-define */
import React, { useState, useContext, useEffect } from 'react';
import {
  Button,
  Col,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
  Modal,
  Form,
  Input,
  Breadcrumb,
} from 'antd';
import moment from 'moment';
import { useFileExploreContext } from 'hooks/useFileExplore';
import { useUploadContext } from 'contexts/UploadContext';
import { useDownloadContext } from 'contexts/DownloadContext';
import { AiOutlineDelete, AiOutlineEdit, AiOutlineFolderAdd } from 'react-icons/ai';
import { getFileIcon, getFileExtension, formatFileSize } from 'utils/upload';
import { TextEllipsis } from 'components';
import LocaleContext from 'locales';
import Upload from './UploadButton';
import Download from './DownloadButton';

/**
 * 파일 탐색기의 선택된 폴더에 대한 하위 아이템 목록 컴포넌트
 * 파일과 폴더 목록을 표시하고 관련 작업을 수행하는 UI
 */
export default function ListPane() {
  // 상태 관리
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [tableHeight, setTableHeight] = useState(window.innerHeight - 250);
  const [viewMode, setViewMode] = useState('detail'); // 'detail' 또는 'icon'
  const { locale } = useContext(LocaleContext);

  // 창 크기 변경 감지를 위함
  useEffect(() => {
    const handleResize = () => {
      setTableHeight(window.innerHeight - 250);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 컨텍스트에서 상태와 함수 가져오기
  const { state, selectFolder } = useFileExploreContext();
  const { items, loading, currentPath, selectedDrive } = state;

  useEffect(() => {
    // 아이템 목록이 변경되면 선택된 행 초기화
    setSelectedRowKeys([]);
  }, [items]);

  // 업로드 컨텍스트에서 기능 가져오기
  const { addFiles, toggleVisibility } = useUploadContext();

  // 다운로드 컨텍스트에서 기능 가져오기
  const { downloadFiles, toggleVisibility: toggleDownloadVisibility } = useDownloadContext();

  // 행 선택 설정
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // 다운로드 핸들러 추가
  const handleDownload = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(locale.resource['txt.file-transfer.msg-select-files']);
      return;
    }

    // 선택된 항목 필터링
    const selectedItems = items.filter((item) => selectedRowKeys.includes(item.key));

    downloadFiles(selectedItems, currentPath, selectedDrive);
    toggleDownloadVisibility(true);
  };

  // 항목 더블클릭 핸들러
  const handleItemDoubleClick = (record) => {
    if (record.type === 'dir') {
      selectFolder(record.path);
    } else {
      // 파일 클릭은 추후 다운로드 기능으로 구현
      // message.info(`파일 '${record.name}' 클릭됨`);
    }
  };

  // 뷰 모드 전환 핸들러
  const toggleViewMode = () => {
    setViewMode(viewMode === 'detail' ? 'icon' : 'detail');
  };

  // 파일 선택 핸들러
  const handleFileSelect = (files) => {
    if (!selectedDrive) {
      message.warning(locale.resource['txt.file-transfer.msg-select-drive']);
      return;
    }

    // Context API를 통해 파일 업로드
    addFiles(files, currentPath, selectedDrive, false);
    toggleVisibility(true); // 패널 표시
  };

  // 폴더 선택 핸들러
  const handleFolderSelect = (files) => {
    if (!selectedDrive) {
      message.warning(locale.resource['txt.file-transfer.msg-select-drive']);
      return;
    }

    // Context API를 통해 폴더 업로드
    addFiles(files, currentPath, selectedDrive, true);
    toggleVisibility(true); // 패널 표시
  };

  return (
    <div className="list-pane">
      <Toolbar
        viewMode={viewMode}
        toggleViewMode={toggleViewMode}
        selectedRowKeys={selectedRowKeys}
        currentPath={currentPath}
        selectedDrive={selectedDrive}
        onFileSelect={handleFileSelect}
        onFolderSelect={handleFolderSelect}
        onDownload={handleDownload}
        locale={locale}
      />

      <Viewer
        tableHeight={tableHeight}
        items={items}
        viewMode={viewMode}
        loading={loading}
        rowSelection={rowSelection}
        onItemDoubleClick={handleItemDoubleClick}
        locale={locale}
      />

      <StatusBar locale={locale} items={items} selectedRowKeys={selectedRowKeys} />
    </div>
  );
}

/**
 * 툴바 컴포넌트 - 파일 작업 버튼 제공 및 경로 표시
 */
function Toolbar({
  viewMode,
  toggleViewMode,
  currentPath,
  selectedRowKeys,
  selectedDrive,
  onFileSelect,
  onFolderSelect,
  onDownload,
  onRefresh,
  locale,
}) {
  // 선택 폴더 변경 핸들러 - Breadcrumb 항목 클릭 시 사용
  const { selectFolder } = useFileExploreContext();

  // 브레드크럼 아이템 생성
  const getBreadcrumbItems = () => {
    if (!selectedDrive || !currentPath) {
      return [];
    }

    // 드라이브 경로를 첫 번째 아이템으로 추가
    const items = [
      {
        title: <span style={{ cursor: 'pointer' }}>{selectedDrive.driveName}</span>,
        key: selectedDrive.drivePath,
        path: selectedDrive.drivePath,
      },
    ];

    // 현재 경로가 드라이브 루트와 같다면 추가 경로 아이템이 필요 없음
    if (currentPath === selectedDrive.drivePath) {
      return items;
    }

    // 드라이브 경로와 현재 경로의 차이를 계산하여 경로 세그먼트 생성
    let relativePath = currentPath;
    if (currentPath.startsWith(selectedDrive.drivePath)) {
      relativePath = currentPath.substring(selectedDrive.drivePath.length);
    }

    // 경로가 /로 시작하지 않으면 추가
    if (!relativePath.startsWith('/')) {
      relativePath = `/${relativePath}`;
    }

    // 경로 세그먼트 분리
    const segments = relativePath.split('/').filter((segment) => segment);

    // 중간 경로 아이템 추가
    let currentSegmentPath = selectedDrive.drivePath;

    segments.forEach((segment, index) => {
      // 이전 경로에 현재 세그먼트 추가
      currentSegmentPath = `${currentSegmentPath}/${segment}`.replace(/\/+/g, '/');

      // 마지막 세그먼트인지 확인
      const isLastSegment = index === segments.length - 1;

      items.push({
        title: (
          <span style={{ cursor: isLastSegment ? 'default' : 'pointer' }}>
            {/* {isLastSegment && <AiOutlineFolder style={{ marginRight: '5px', fontSize: '14px' }} />} */}
            {segment}
          </span>
        ),
        key: currentSegmentPath,
        path: currentSegmentPath,
      });
    });

    return items;
  };

  // 브레드크럼 항목 클릭 핸들러
  const handleBreadcrumbClick = (e, path) => {
    // 경로가 현재 경로와 다를 때만 처리
    if (path !== currentPath) {
      selectFolder(path);
    }
  };

  return (
    <Row
      style={{
        padding: '8px 12px',
        alignItems: 'center',
        flexWrap: 'nowrap',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      <Col
        style={{
          flex: '1',
          overflow: 'hidden',
          marginRight: '16px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {selectedDrive && (
          <>
            <Typography.Text
              style={{
                fontSize: '13px',
                marginRight: '8px',
                whiteSpace: 'nowrap',
                lineHeight: '30px',
              }}
            >
              {locale.resource['txt.file-transfer.current-location']}:
            </Typography.Text>

            <Breadcrumb
              routes={getBreadcrumbItems()}
              itemRender={(item, params, items) => {
                const isLastItem = items.indexOf(item) === items.length - 1; // 마지막 항목은 클릭 불가

                if (isLastItem) {
                  return <span style={{ fontSize: '13px', lineHeight: '30px' }}>{item.title}</span>;
                }
                return (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid
                  <a
                    onClick={(e) => handleBreadcrumbClick(e, item.path)}
                    style={{ fontSize: '13px', lineHeight: '30px' }}
                  >
                    {item.title}
                  </a>
                );
              }}
              style={{ fontSize: '13px' }}
              separator={<span style={{ margin: '0 2px' }}>/</span>}
            />
          </>
        )}
      </Col>
      <Col>
        <Space>
          <Upload />
          <Download selectedRowKeys={selectedRowKeys} onDownload={onDownload} />
          {/* // TODO 나중에 구현할 기능들 */}
          {/* 
          <NewFolder folderPath={currentPath} onSuccess={onRefresh} />

          <Rename
            selectedKeys={selectedRowKeys}
            disabled={selectedRowKeys.length !== 1}
            onSuccess={onRefresh}
          />

          <Delete
            selectedKeys={selectedRowKeys}
            disabled={selectedRowKeys.length === 0}
            onSuccess={onRefresh}
          /> */}

          {/* <Tooltip title={`${viewMode === 'detail' ? '아이콘' : '상세'} 보기로 전환`}>
            <Button
              type="text"
              icon={
                viewMode === 'detail' ? (
                  <AiOutlineAppstore className="anticon" />
                ) : (
                  <AiOutlineUnorderedList className="anticon" />
                )
              }
              onClick={toggleViewMode}
            />
          </Tooltip> */}
        </Space>
      </Col>
    </Row>
  );
}

/**
 * 뷰어 컴포넌트 - 파일/폴더 목록 표시
 */
function Viewer({
  items,
  viewMode,
  loading,
  rowSelection,
  onItemDoubleClick,
  locale,
  tableHeight,
}) {
  // 상세 보기 모드 (테이블)
  if (viewMode === 'detail') {
    const [sortConfig, setSortConfig] = useState({
      key: null, // 기본 정렬 키
      direction: null, // 기본 정렬 방향
    });

    // 데이터를 컴포넌트 내부에서 정렬
    const sortedItems = React.useMemo(() => {
      // 데이터 복사
      const sortableItems = [...items];

      // 1. 기본 정렬: 폴더가 항상 파일보다 먼저 오도록 정렬한다.
      sortableItems.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === 'dir' ? -1 : 1;
        }
        // 기본 정렬 기준: 이름 (정렬 설정이 없을 때)
        if (!sortConfig.key || !sortConfig.direction) {
          return a.name.localeCompare(b.name);
        }
        return 0; // 아래에서 추가 정렬
      });

      // 2. 정렬 설정이 있는 경우에만 추가 정렬 수행
      if (sortConfig.key && sortConfig.direction) {
        sortableItems.sort((a, b) => {
          // 항상 폴더가 파일보다 먼저 오도록 함
          if (a.type !== b.type) {
            return a.type === 'dir' ? -1 : 1;
          }
          // 타입이 같을 때만 선택된 열로 정렬
          const { key, direction } = sortConfig;
          let comparison = 0;

          switch (key) {
            case 'name':
              comparison = a.name.localeCompare(b.name);
              break;
            case 'whenModified':
              comparison = new Date(a.whenModified) - new Date(b.whenModified);
              break;
            case 'size':
              comparison = a.size - b.size;
              break;
            default:
              comparison = 0;
          }
          // 정렬 방향 적용
          return direction === 'ascend' ? comparison : -comparison;
        });
      }
      return sortableItems;
    }, [items, sortConfig]);

    // 컬럼 클릭 시 정렬 변경 핸들러
    const handleTableChange = (pagination, filters, sorter) => {
      // '유형' 컬럼 정렬하지 않음.
      if (sorter.field === 'type') return;

      // 정렬 설정 업데이트
      setSortConfig({
        key: sorter.field,
        direction: sorter.order, // order는 'ascend', 'descend', undefined(정렬 없음) 중 하나
      });
    };

    return (
      <Table
        rowKey="key"
        loading={loading}
        rowSelection={rowSelection}
        pagination={false}
        dataSource={sortedItems}
        onChange={handleTableChange} // 테이블 변경 이벤트 처리
        onRow={(record) => ({
          onDoubleClick: () => onItemDoubleClick(record),
        })}
        sortDirections={['ascend', 'descend', null]}
        scroll={{ y: tableHeight }}
        columns={[
          {
            title: locale.resource['txt.file-transfer.name'],
            dataIndex: 'name',
            sorter: true, // 단순히 정렬 가능함을 표시
            sortOrder: sortConfig.key === 'name' ? sortConfig.direction : null,
            render: (value, record) => (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'default',
                  userSelect: 'none',
                }}
              >
                <Typography style={{ fontSize: '18px', marginRight: '8px' }}>
                  {getFileIcon(record.type === 'dir' ? 'folder' : getFileExtension(record.name))}
                </Typography>
                <TextEllipsis text={value} />
              </div>
            ),
          },
          {
            title: locale.resource['txt.file-transfer.modified-date'],
            // title: '수정한 날짜',
            dataIndex: 'whenModified',
            sorter: true, // 단순히 정렬 가능함을 표시
            sortOrder: sortConfig.key === 'whenModified' ? sortConfig.direction : null,
            width: 160,
            render: (value) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
          },
          {
            title: locale.resource['txt.file-transfer.type'],
            // title: '유형',
            dataIndex: 'type',
            width: 80,
            // 유형으로는 정렬하지 않음
            sorter: false,
            render: (value) =>
              locale.resource[
                value === 'dir' ? 'txt.file-transfer.folder' : 'txt.file-transfer.file'
              ],
          },
          {
            title: locale.resource['txt.file-transfer.size'],
            // title: '크기',
            dataIndex: 'size',
            sorter: true, // 단순히 정렬 가능함을 표시
            sortOrder: sortConfig.key === 'size' ? sortConfig.direction : null,
            width: 110,
            align: 'right',
            render: (value, record) => (record.type === 'dir' ? '-' : formatFileSize(value)),
          },
        ]}
      />
    );
  }
}

/**
 * 상태바 컴포넌트 - 항목 수와 선택된 항목 수 표시
 */
function StatusBar({ items, selectedRowKeys, locale }) {
  return (
    <div className="ant-table-footer">
      <Space size="large">
        <Typography.Text type="secondary">
          {items.length}
          {locale.resource['txt.file-transfer.items']}
        </Typography.Text>
        {selectedRowKeys.length > 0 && (
          <Typography.Text type="secondary">
            {selectedRowKeys.length}
            {locale.resource['txt.file-transfer.selected']}
          </Typography.Text>
        )}
      </Space>
    </div>
  );
}

/**
 * 새 폴더 생성 컴포넌트 (나중에 구현)
 */
function NewFolder({ folderPath, onSuccess }) {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
  };

  const onOk = async () => {
    try {
      const values = await form.validateFields();
      console.log('새 폴더 생성:', values.name, '위치:', folderPath);

      // TODO: 폴더 생성 API 연동 구현
      message.success(`'${values.name}' 폴더가 생성되었습니다.`);
      onSuccess();

      onCancel();
    } catch (error) {
      console.error('폴더 생성 실패:', error);
    }
  };

  return (
    <>
      <Button
        type="text"
        icon={<AiOutlineFolderAdd className="anticon" />}
        onClick={() => setVisible(true)}
      >
        새 폴더
      </Button>

      <Modal
        title="새 폴더"
        visible={visible}
        onCancel={onCancel}
        okText="생성"
        cancelText="취소"
        onOk={onOk}
        width={360}
      >
        <Form form={form}>
          <Form.Item
            name="name"
            label="폴더 이름"
            rules={[
              { required: true, message: '폴더 이름을 입력해주세요' },
              { max: 255, message: '폴더 이름은 255자를 초과할 수 없습니다' },
            ]}
          >
            <Input autoFocus placeholder="새 폴더 이름" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

/**
 * 이름 변경 컴포넌트 (나중에 구현)
 */
function Rename({ selectedKeys, disabled, onSuccess }) {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
  };

  const onOk = async () => {
    try {
      const values = await form.validateFields();
      console.log('이름 변경:', selectedKeys[0], '→', values.newName);

      // TODO: 이름 변경 API 연동 구현
      message.success(`이름이 '${values.newName}'로 변경되었습니다.`);
      onSuccess();

      onCancel();
    } catch (error) {
      console.error('이름 변경 실패:', error);
    }
  };

  return (
    <>
      <Button
        type="text"
        icon={<AiOutlineEdit className="anticon" />}
        onClick={() => setVisible(true)}
        disabled={disabled}
      >
        이름 변경
      </Button>

      <Modal
        title="이름 변경"
        visible={visible}
        onCancel={onCancel}
        okText="변경"
        cancelText="취소"
        onOk={onOk}
        width={360}
      >
        <Form form={form}>
          <Form.Item
            name="newName"
            label="새 이름"
            rules={[
              { required: true, message: '새 이름을 입력해주세요' },
              { max: 255, message: '이름은 255자를 초과할 수 없습니다' },
            ]}
          >
            <Input autoFocus placeholder="새 이름" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

/**
 * 삭제 컴포넌트 (나중에 구현)
 */
function Delete({ selectedKeys, disabled, onSuccess }) {
  const handleDelete = () => {
    if (selectedKeys.length === 0) {
      message.warning('삭제할 항목을 선택해주세요.');
      return;
    }

    Modal.confirm({
      title: '삭제 확인',
      content: `선택한 ${selectedKeys.length}개 항목을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          // TODO: 삭제 API 연동 구현
          console.log('삭제할 항목:', selectedKeys);
          message.success('선택한 항목이 삭제되었습니다.');
          onSuccess();
        } catch (error) {
          console.error('삭제 실패:', error);
          message.error('삭제 중 오류가 발생했습니다.');
        }
      },
    });
  };

  return (
    <Button
      type="text"
      icon={<AiOutlineDelete className="anticon" />}
      onClick={handleDelete}
      disabled={disabled}
    >
      삭제
    </Button>
  );
}
